import { BillingBreakdownLine } from '../components/BillingBreakdownDisplay';

export interface AppendixRateRange {
  from: number;
  to: number | null;
  rate: number;
}

export interface BillingPreview {
  lines: BillingBreakdownLine[];
  total_amount: number;
}

function parseRangeTo(value: unknown): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
}

function normalizeRanges(ranges: Array<{ from?: number | string; to?: number | string | null; rate?: number | string }>): AppendixRateRange[] {
  return ranges
    .filter((r) => r && Number(r.rate) >= 0)
    .map((r) => ({
      from: Number(r.from ?? 1),
      to: parseRangeTo(r.to),
      rate: Number(r.rate ?? 0),
    }))
    .sort((a, b) => a.from - b.from);
}

function getAppendixRateRanges(custom: Record<string, unknown>): AppendixRateRange[] {
  const ranges = custom.appendix_c_rate_ranges;
  if (Array.isArray(ranges) && ranges.length > 0) {
    return normalizeRanges(ranges);
  }
  const singleRate = Number(custom.appendix_c_rate ?? 0);
  if (singleRate > 0) {
    return [{ from: 1, to: null, rate: singleRate }];
  }
  return [];
}

function applyTieredRanges(totalPages: number, ranges: AppendixRateRange[]): { lines: BillingBreakdownLine[]; total: number } {
  const lines: BillingBreakdownLine[] = [];
  let total = 0;
  let pagesAccountedFor = 0;

  for (const range of ranges) {
    if (pagesAccountedFor >= totalPages) break;

    const from = Math.max(1, range.from);
    const to = range.to;
    const rate = range.rate;

    const startPage = Math.max(pagesAccountedFor + 1, from);
    const endPage = to !== null ? Math.min(totalPages, to) : totalPages;
    const quantity = Math.max(0, endPage - startPage + 1);

    if (quantity <= 0) continue;

    const amount = Math.round(quantity * rate * 100) / 100;
    const label = to !== null ? `Appendix pages (${from}–${to})` : `Appendix pages (${from}+)`;

    lines.push({ label, quantity, rate, amount });
    total += amount;
    pagesAccountedFor += quantity;
  }

  return { lines, total: Math.round(total * 100) / 100 };
}

export function calculatePageBasedPreview(
  custom: Record<string, unknown>,
  totalPages: number,
  reviewedPages: number,
  appendixIncluded: boolean,
  defaultPageRate?: number
): BillingPreview | null {
  if (!totalPages || !reviewedPages || reviewedPages > totalPages) return null;

  const pageRate = Number(defaultPageRate ?? custom.rate ?? 0);
  const lines: BillingBreakdownLine[] = [];

  const reviewedAmount = Math.round(reviewedPages * pageRate * 100) / 100;
  lines.push({
    label: 'Reviewed pages',
    quantity: reviewedPages,
    rate: pageRate,
    amount: reviewedAmount,
  });

  let totalAmount = reviewedAmount;
  const appendixPages = Math.max(0, totalPages - reviewedPages);

  if (appendixIncluded && appendixPages > 0) {
    const tierResult = applyTieredRanges(appendixPages, getAppendixRateRanges(custom));
    lines.push(...tierResult.lines);
    totalAmount += tierResult.total;
  }

  return {
    lines,
    total_amount: Math.round(totalAmount * 100) / 100,
  };
}

export function calculateConsolidationPreview(
  custom: Record<string, unknown>,
  totalPages: number,
  appendixIncluded: boolean,
  defaultPageRate?: number
): BillingPreview | null {
  if (!totalPages) return null;

  const pageRate = Number(defaultPageRate ?? custom.rate ?? 0);
  const appendixRate = Number(custom.appendix_c_rate ?? 0);
  const lines: BillingBreakdownLine[] = [];

  const mainAmount = Math.round(totalPages * pageRate * 100) / 100;
  lines.push({
    label: 'Consolidation pages',
    quantity: totalPages,
    rate: pageRate,
    amount: mainAmount,
  });

  let totalAmount = mainAmount;

  if (appendixIncluded && appendixRate > 0) {
    const appendixAmount = Math.round(totalPages * appendixRate * 100) / 100;
    lines.push({
      label: 'Appendix C',
      quantity: totalPages,
      rate: appendixRate,
      amount: appendixAmount,
    });
    totalAmount += appendixAmount;
  }

  return {
    lines,
    total_amount: Math.round(totalAmount * 100) / 100,
  };
}

export function calculateBillingPreview(
  billingType: string | undefined,
  custom: Record<string, unknown> | undefined,
  formData: {
    total_pages: string;
    reviewed_pages: string;
    appendix_included: boolean;
  },
  defaultPageRate?: number
): BillingPreview | null {
  if (!billingType || !custom) return null;

  const totalPages = parseInt(formData.total_pages, 10);
  const reviewedPages = parseInt(formData.reviewed_pages, 10);

  if (billingType === 'Page Based') {
    return calculatePageBasedPreview(custom, totalPages, reviewedPages, formData.appendix_included, defaultPageRate);
  }
  if (billingType === 'Consolidation') {
    return calculateConsolidationPreview(custom, totalPages, formData.appendix_included, defaultPageRate);
  }
  return null;
}
